import { DocumentOnView } from 'presentation/document/documentOnView';
import { DocumentsOnIssueView } from 'presentation/document/documentsOnIssueView';
import { HashMap } from 'shared/types/commonView';
import { AttachmentCounts } from 'shared/domain/issue/issueModel';
import { isDefinedValue } from 'shared/utils/value';
import {
  attachmentCountsToPdfCounts,
  classifyDocumentsForPdfEstimate,
  PdfDocumentCounts,
  toPdfClassifiableDocument,
} from './classifyDocumentsForPdfEstimate';

/**
 * Document counts the estimate rows cannot take from the issue model: from the open
 * issue view (its live documents), or counted by the worker for a report of many issues.
 */
export type ReportIssueStatsOverrides = {
  /** The issue's own documents, split the way the PDF prints them. */
  primaryDocuments?: PdfDocumentCounts;
  /** The documents of each event, by event id. */
  eventDocuments?: HashMap<PdfDocumentCounts>;
  /** The documents of each incoming email, by email id. */
  emailDocuments?: HashMap<PdfDocumentCounts>;
};

function classifyDocumentsOnView(documents: DocumentOnView[]): PdfDocumentCounts {
  return classifyDocumentsForPdfEstimate(documents.map(toPdfClassifiableDocument).filter(isDefinedValue));
}

function classifyDocumentsOnViewByParent(documentsByParent: HashMap<DocumentOnView[]>): HashMap<PdfDocumentCounts> {
  return Object.fromEntries(
    Object.entries(documentsByParent).map(([parentId, documents]) => [parentId, classifyDocumentsOnView(documents)])
  );
}

export function buildReportIssueStatsOverridesFromDocuments(
  documents: DocumentsOnIssueView
): ReportIssueStatsOverrides {
  return {
    primaryDocuments: classifyDocumentsOnView(documents.issueDocuments),
    eventDocuments: classifyDocumentsOnViewByParent(documents.eventDocuments),
    emailDocuments: classifyDocumentsOnViewByParent(documents.incomingEmailMessageDocuments),
  };
}

/** The issue's own attachments counted by the worker, for a report of many issues. */
export function buildReportIssueStatsOverridesFromAttachmentCounts(
  counts: AttachmentCounts | undefined
): ReportIssueStatsOverrides {
  return counts ? { primaryDocuments: attachmentCountsToPdfCounts(counts) } : {};
}
